import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { supportFetch as fetch } from '../lib/supportTransport';

const API = process.env.REACT_APP_BACKEND_URL;

export const SupportHandoffIntake = ({ sessionId, visitorType, onConnect, onCancel }) => {
  const [audiences, setAudiences] = useState([]);
  const [form, setForm] = useState({ visitor_name: '', visitor_type: visitorType || 'other', topic_key: 'general' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setError('');
    try {
      const response = await fetch(`${API}/api/support-chat/topics`);
      if (!response.ok) throw new Error();
      setAudiences(await response.json());
    } catch { setError('Topics could not be loaded. Please retry.'); }
  };
  useEffect(() => { load(); }, []);
  const submit = async (event) => {
    event.preventDefault();
    if (busy || !form.visitor_name.trim()) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/api/support-chat/intake`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, ...form }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details.');
      const connected = await onConnect({ ...data, questions: topics });
      if (!connected) setError('The connection request could not be completed. Your details are saved; please retry or email support.');
    } catch (err) { setError(err.message || 'Could not save your details. Please retry.'); }
    finally { setBusy(false); }
  };
  const topics = audiences.find(a => a.key === form.visitor_type)?.topics || [];
  const selectClass = 'w-full min-w-0 bg-zinc-900 border border-zinc-600 rounded p-2 text-xs text-white';
  return (
    <form onSubmit={submit} className="space-y-3 border-t border-zinc-600 pt-3" data-testid="support-handoff-intake">
      <p className="text-sm text-white font-semibold" data-testid="support-handoff-intake-title">Before we connect you</p>
      <label className="block text-xs text-zinc-300">Your name
        <Input required maxLength={100} value={form.visitor_name} onChange={e => setForm({ ...form, visitor_name: e.target.value })}
          autoComplete="given-name" disabled={busy} className="mt-1 bg-zinc-900 text-white border-zinc-600" data-testid="support-handoff-name" />
      </label>
      <label className="block text-xs text-zinc-300">I am a
        <select value={form.visitor_type} onChange={e => setForm({ ...form, visitor_type: e.target.value, topic_key: 'general' })}
          disabled={busy || !audiences.length} className={selectClass} data-testid="support-handoff-audience">
          {audiences.map(a => <option key={a.key} value={a.key} data-testid={`support-handoff-audience-${a.key}`}>{a.label}</option>)}
        </select>
      </label>
      <label className="block text-xs text-zinc-300">Topic
        <select value={form.topic_key} onChange={e => setForm({ ...form, topic_key: e.target.value })}
          disabled={busy || !topics.length} className={selectClass} data-testid="support-handoff-topic">
          {topics.map(t => <option key={t.key} value={t.key} data-testid={`support-handoff-topic-${t.key}`}>{t.label}</option>)}
        </select>
      </label>
      {error && <p role="alert" className="text-xs text-red-300" data-testid="support-handoff-error">{error}</p>}
      {!audiences.length && <Button type="button" onClick={load} data-testid="support-handoff-retry">Retry topics</Button>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy || !audiences.length || !form.visitor_name.trim()} className="bg-[#C9A962] text-black hover:bg-[#dac080]" data-testid="support-handoff-connect">{busy ? 'Connecting…' : 'Connect to agent'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} className="text-zinc-300" data-testid="support-handoff-cancel">Cancel</Button>
      </div>
    </form>
  );
};